import { useCallback, useMemo, useState } from 'react';
import { useGetBettingGameQuery } from './gql/getBettingGame.generated';
import { Currency, Match } from '@Global/apollo/types';
import { shortenTeamName } from '@Global/utils/shortenTeamName';
import { useMatchesLazy } from '../getMatches/useMatches';

/**
 * Статистика одного матча игрока
 * Содержит результаты, очки и дату матча
 */
type PlayerMatchStats = {
  id: string;
  homeTeamResult: string;
  awayTeamResult: string;
  playerPoints: number;
  date: string;
};

/**
 * Информация об игроке в betting game
 * Включает данные карточки, статистику и последние матчи
 */
type BattleBetPlayer = {
  cardId: string;
  id: string;
  name: string;
  image: string;
  position: string;
  avgScoreForLast5: string | number;
  bettingMatchDate: string;
  isPlayerInHomeTeam: boolean;
  homeTeamName: string;
  awayTeamName: string;
  homeTeamWins: number;
  awayTeamWins: number;
  last5Matches: PlayerMatchStats[];
  homeOdds: number;
  awayOdds: number;
};

export type BattleBetOutcome = {
  id: string;
  result?: number;
  poolSum?: {
    amount: string;
    currency: Currency;
    amountInUsdt: string;
  };
  betsNum?: number | null;
  subject: {
    card: {
      player: {
        id: string;
      };
    };
  };
  matches: (Match & { totalPoints: number })[];
};

type BattleBet = {
  id: string;
  outcome: {
    id: string;
  };
  sum: {
    currency: Currency;
    amount: number;
  };
};

/**
 * Упрощенный тип подарка для BattleData
 */
type BattleGift = {
  id: string;
  isClaimed: boolean;
  expiresAt?: any;
  [key: string]: any;
};

/**
 * Тип для ставки в баттле
 */
type BattleOrder = {
  id: string;
  user: {
    name?: string;
    isJackpotParticipant: boolean;
  };
  sum: {
    amount: string;
    currency: Currency;
  };
  player?: {
    id: string;
    name: string;
    image: string;
  };
  createdAt?: string;
};

/**
 * Полные данные о betting game
 * Содержит основную информацию и статистику участников
 */
export type BattleData = {
  id: string;
  name: string;
  nameEn?: string;
  nameRu?: string;
  totalBank: number;
  players: BattleBetPlayer[];
  acceptedCurrencies: Currency[];
  isStarted: boolean;
  isCompleted: boolean;
  isCanceled: boolean;
  outcomes: BattleBetOutcome[];
  myBets: BattleBet[];
  myGifts: BattleGift[];
  orders: BattleOrder[];
  hasNextOrdersPage: boolean;
  fetchNextOrdersPage: (after: string) => Promise<void>;
  endCursor: string;
  period: {
    from: string;
    to: string;
  };
  potentialPrize: number;
};

/**
 * Форматирует статистику матча для отображения
 * @param match - Данные матча
 * @param playerTeamIds - ID команд игрока
 * @param playerScoreByMatch - Очки игрока по матчам
 */
const formatMatchStats = (
  match: Match,
  playerTeamIds: string[],
  playerScoreByMatch: Array<{ matchId: string; points: number }>,
): PlayerMatchStats => {
  const isPlayerInHomeTeam = playerTeamIds.includes(match.homeTeam.id);
  const playerTeam = isPlayerInHomeTeam ? match.homeTeam : match.awayTeam;
  const opposingTeam = isPlayerInHomeTeam ? match.awayTeam : match.homeTeam;
  const [homeScore, awayScore] = match.score?.split(':') || ['', ''];

  return {
    id: match.id,
    homeTeamResult: `${isPlayerInHomeTeam ? homeScore : awayScore} ${shortenTeamName(playerTeam.name)}`,
    awayTeamResult: `${!isPlayerInHomeTeam ? homeScore : awayScore} ${shortenTeamName(opposingTeam.name)}`,
    playerPoints: playerScoreByMatch.find((score) => score.matchId === match.id)?.points || 0,
    date: match.matchDay,
  };
};

/**
 * Хук для получения и управления данными betting game
 * @param id - ID betting game
 * @returns Объект с данными игры, состоянием загрузки и функциями управления
 */
export const useGetBettingGame = (id: string) => {
  const [loadingMoreOrders, setLoadingMoreOrders] = useState(false);

  const {
    data,
    loading,
    error,
    refetch,
    fetchMore: fetchMoreOrders,
  } = useGetBettingGameQuery({
    variables: {
      id,
      playerIds: [],
      withStats: false,
    },
    skip: !id,
  });

  const bettingGame = data?.node?.__typename === 'BettingGame' ? data.node : null;

  // Мемоизируем ID участников
  const participantIds = useMemo(
    () => ({
      firstPlayer: bettingGame?.outcomes[0]?.subject.card.player.id || '',
      secondPlayer: bettingGame?.outcomes[1]?.subject.card.player.id || '',
    }),
    [bettingGame?.outcomes],
  );

  // Ленивые запросы для получения матчей первого участника
  const {
    getMatches: getFirstPlayerMatches,
    matches: firstPlayerMatches,
    loading: isLoadingFirstPlayerStats,
  } = useMatchesLazy({
    first: 5,
    players: [participantIds.firstPlayer],
  });

  // Ленивые запросы для получения матчей второго участника
  const {
    getMatches: getSecondPlayerMatches,
    matches: secondPlayerMatches,
    loading: isLoadingSecondPlayerStats,
  } = useMatchesLazy({
    first: 5,
    players: [participantIds.secondPlayer],
  });

  // Форматирование данных участника
  const formatPlayerData = useCallback(
    (
      outcome: NonNullable<typeof bettingGame>['outcomes'][0],
      matchHistory: Match[] | undefined,
    ): BattleBetPlayer | null => {
      if (outcome.subject.__typename !== 'PlayerCardBettingGame') return null;

      const player = outcome.subject.card.player;
      const playerTeamIds = player.teams.edges.map((team) => team.node.team.id);

      const matchesStats = matchHistory
        ? matchHistory.map((match) => formatMatchStats(match, playerTeamIds, player.scoreByMatch))
        : [];

      // Определяем, в какой команде играет игрок в матче ставки
      const bettingMatch = outcome.matches[0];
      const isPlayerInHomeTeam = playerTeamIds.includes(bettingMatch?.homeTeam.id || '');

      return {
        cardId: outcome.subject.card.id,
        id: player.id,
        name: player.name,
        position: player.teams.edges[0]?.node.position || '',
        avgScoreForLast5: player.avgScoreForLast5 || '',
        image: outcome.subject.card.image,
        bettingMatchDate: bettingMatch?.matchDay || '',
        isPlayerInHomeTeam,
        homeTeamName: bettingMatch?.homeTeam.name || '',
        awayTeamName: bettingMatch?.awayTeam.name || '',
        homeTeamWins: 0,
        awayTeamWins: 0,
        last5Matches: matchesStats,
        homeOdds: outcome.matches[0]?.odds?.find((o) => o.name === 'MatchWinner.Home')?.probability || 0,
        awayOdds: outcome.matches[0]?.odds?.find((o) => o.name === 'MatchWinner.Away')?.probability || 0,
      };
    },
    [],
  );

  const fetchNextOrdersPage = useCallback(async () => {
    // Проверяем состояние перед запросом
    if (!bettingGame?.bets.pageInfo.hasNextPage || loadingMoreOrders) {
      return;
    }

    try {
      setLoadingMoreOrders(true);
      await fetchMoreOrders({
        variables: {
          id,
          after: bettingGame.bets.pageInfo.endCursor,
        },
      });
    } catch (error) {
      console.error('❌ Error fetching more orders:', error);
    } finally {
      setLoadingMoreOrders(false);
    }
  }, [bettingGame, fetchMoreOrders, id, loadingMoreOrders]);

  // Трансформация данных для фронтенда
  const bettingGameData = useMemo(() => {
    if (!bettingGame) return null;

    const players = [
      formatPlayerData(bettingGame.outcomes[0], firstPlayerMatches),
      formatPlayerData(bettingGame.outcomes[1], secondPlayerMatches),
    ].filter((player): player is BattleBetPlayer => player !== null);

    const orders = bettingGame.bets.edges.map((edge) => {
      const node = edge.node;
      return {
        id: node.id,
        user: {
          name: node.account.name,
          isJackpotParticipant: node.isJackpotParticipant,
        },
        sum: {
          amount: node.sum.amount.toString(),
          currency: node.sum.currency,
        },
        createdAt: node.createdAt,
        player: {
          id: node.outcome?.subject?.card?.player?.id,
          name: node.outcome?.subject?.card?.player?.name,
          image: node.outcome?.subject?.card?.image,
        },
      };
    });

    return {
      ...bettingGame,
      nameEn: (bettingGame as any).nameEn,
      nameRu: (bettingGame as any).nameRu,
      players,
      orders,
      hasNextOrdersPage: bettingGame.bets.pageInfo.hasNextPage,
      fetchNextOrdersPage,
      endCursor: bettingGame.bets.pageInfo.endCursor,
    };
  }, [bettingGame, firstPlayerMatches, secondPlayerMatches, formatPlayerData, fetchNextOrdersPage]);

  // Загрузка статистики участника
  const loadPlayerStats = useCallback(
    (playerId: string) => {
      if (playerId === participantIds.firstPlayer) {
        getFirstPlayerMatches();
      } else if (playerId === participantIds.secondPlayer) {
        getSecondPlayerMatches();
      }
    },
    [participantIds, getFirstPlayerMatches, getSecondPlayerMatches],
  );

  // Мемоизируем ID игроков для стабильности
  const playerIds = useMemo(() => {
    if (!bettingGame?.outcomes) return [];

    const currentFirstPlayer = bettingGame.outcomes[0]?.subject.card.player.id || '';
    const currentSecondPlayer = bettingGame.outcomes[1]?.subject.card.player.id || '';
    return [currentFirstPlayer, currentSecondPlayer].filter(Boolean);
  }, [bettingGame?.outcomes]);

  // Функция для программного получения данных с playerStats
  const fetchBettingGameWithStats = useCallback(
    async (battleId: string | null) => {
      // Используем переданный battleId или текущий id
      const gameId = battleId || id;
      if (!gameId) {
        return;
      }

      // ВАЖНО: Передаем всех игроков, но в battlesContext.tsx будем фильтровать статистику по игрокам
      await refetch({
        id: gameId,
        playerIds,
        withStats: true,
      });
    },
    [refetch, id, playerIds], // Зависим от мемоизированных playerIds
  );

  return {
    battle: bettingGameData,
    loading,
    error,
    loadPlayerStats,
    isLoadingMoreStats: isLoadingFirstPlayerStats || isLoadingSecondPlayerStats,
    refetch,
    loadingMoreOrders,
    fetchBettingGameWithStats,
  };
};
